import { useContext, useMemo, useState } from 'react';
import './sidebar.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthCtx } from '../../context/AuthCtxProvider';
import { JTranslation } from '../../helpers/jTranslationHelper';
import { getUserPermissionKeys } from '../../utils/common';
import { useQueryClient } from 'react-query';
import { OrgPermissionKeys, PermissionKeys } from '../../constants/constants';
import { routes } from '../../routes/allRoutes';

type SIDE_BAR = {
    title: string;
    slug: string;
    icon: string;
    path: string;
    requiredPermission?: PermissionKeys | OrgPermissionKeys;
    onlyForOrgAdmin?: boolean;
    children?: SIDE_BAR[];
};

function Sidebar() {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { userType, isAdminToOrgAdmin } = useContext(AuthCtx);

    // openSlugs[depth] is the slug open at that depth (accordion per depth)
    const [openSlugs, setOpenSlugs] = useState<string[]>([]);

    const adminSideBarRoutes: SIDE_BAR[] = [
        {
            title: 'Dashboard',
            slug: 'dashboard',
            icon: 'ri-home-3-line',
            path: routes.dashboard,
        },
        {
            title: 'Organization',
            slug: 'organization',
            icon: 'ri-group-2-line',
            path: routes.organizationList,
            requiredPermission: PermissionKeys.MANAGE_ORGANIZATION,
        },
        {
            title: 'Staff',
            slug: 'staffs',
            icon: 'ri-group-line',
            path: routes.staff,
            requiredPermission: PermissionKeys.MANAGE_STAFF,
            children: [
                {
                    title: 'List',
                    slug: 'list',
                    icon: 'ri-file-list-line',
                    path: '/list',
                },
                // {
                //     title: 'Roles',
                //     slug: 'roles',
                //     icon: 'ri-user-settings-line',
                //     path: '/roles',
                // },
                {
                    title: 'Permissions',
                    slug: 'permissions',
                    icon: 'ri-shield-keyhole-line',
                    path: '/permissions',
                    requiredPermission: PermissionKeys.MANAGE_PERMISSION,
                },
            ],
        },
        {
            title: 'Settings',
            slug: 'settings',
            icon: 'ri-settings-3-line',
            path: routes.settings,
        },
        {
            title: 'Subscriptions',
            slug: 'subscriptions',
            icon: 'ri-wallet-line',
            path: routes.subscriptions,
            children: [
                {
                    title: 'Plans',
                    slug: 'plans',
                    icon: 'ri-price-tag-3-line',
                    path: '/plans',
                },
                {
                    title: 'Features',
                    slug: 'features',
                    icon: 'ri-stack-line',
                    path: '/features',
                    requiredPermission: PermissionKeys.MANAGE_PERMISSION,
                },
            ],
        },
        {
            title: 'Report',
            slug: 'report',
            icon: 'ri-file-chart-line',
            path: routes.report,
            requiredPermission: PermissionKeys.MANAGE_REPORTS,
        },
        {
            title: 'Help',
            slug: 'help',
            icon: 'ri-question-line',
            path: routes.help,
        },
    ];

    const orgSideBarRoutes: SIDE_BAR[] = [
        {
            title: 'Dashboard',
            slug: 'dashboard',
            icon: 'ri-home-3-line',
            path: routes.orgDashboard,
        },
        {
            title: 'Venues',
            slug: 'venues',
            icon: 'ri-group-2-line',
            path: routes.orgVenue,
            requiredPermission: OrgPermissionKeys.MANAGE_VENUE,
            children: [
                {
                    title: 'List',
                    slug: 'list',
                    icon: 'ri-file-list-line',
                    path: '/list',
                },
                {
                    title: 'Users',
                    slug: 'users',
                    icon: 'ri-group-line',
                    path: '/users',
                    requiredPermission: OrgPermissionKeys.MANAGE_VENUE_STAFF,
                },
                {
                    title: 'Permissions',
                    slug: 'permissions',
                    icon: 'ri-shield-keyhole-line',
                    path: '/permissions',
                    requiredPermission: OrgPermissionKeys.MANAGE_VENUE_PERMISSION,
                },
                {
                    title: 'F&B Menu',
                    slug: 'fbmenu',
                    icon: 'ri-file-list-3-line',
                    path: '/fbmenu',
                    requiredPermission: OrgPermissionKeys.MANAGE_VENUE_MENU,
                    children: [
                        {
                            title: 'List',
                            slug: 'list',
                            icon: 'ri-list-unordered',
                            path: '',
                        },
                        {
                            title: 'Bulk Upload',
                            slug: 'bulk-upload',
                            icon: 'ri-upload-cloud-2-line',
                            path: '/bulk-upload',
                        },
                    ],
                },
                {
                    title: 'Schedules',
                    slug: 'schedules',
                    icon: 'ri-calendar-event-line',
                    path: '/schedules',
                    requiredPermission: OrgPermissionKeys.MANAGE_VENUE_SCHEDULE,
                },
                {
                    title: 'Settings',
                    slug: 'settings',
                    icon: 'ri-settings-3-line',
                    path: '/settings',
                    requiredPermission: OrgPermissionKeys.MANAGE_VENUE_SETTINGS,
                },
            ],
        },
        {
            title: 'Staff',
            slug: 'staffs',
            icon: 'ri-group-line',
            path: routes.orgStaff,
            requiredPermission: OrgPermissionKeys.MANAGE_STAFF,
            children: [
                {
                    title: 'List',
                    slug: 'list',
                    icon: 'ri-file-list-line',
                    path: '/list',
                },
                {
                    title: 'Permissions',
                    slug: 'permissions',
                    icon: 'ri-shield-keyhole-line',
                    path: '/permissions',
                    requiredPermission: OrgPermissionKeys.MANAGE_PERMISSION,
                },
            ],
        },
        {
            title: 'Settings',
            slug: 'settings',
            icon: 'ri-settings-3-line',
            path: routes.orgSettings,
        },
        {
            title: 'Subscriptions',
            slug: 'subscriptions',
            icon: 'ri-wallet-line',
            path: routes.orgSubscriptions,
            // onlyForOrgAdmin: true,
        },
        {
            title: 'Report',
            slug: 'report',
            icon: 'ri-file-chart-line',
            path: routes.orgReport,
        },
        {
            title: 'Help',
            slug: 'help',
            icon: 'ri-question-line',
            path: routes.orgHelp,
        },
    ];

    const sideBarRoutes = useMemo(() => {
        if (window.location.pathname.includes('/admin') && (userType === 'adminStaff' || userType === 'superAdmin'))
            return adminSideBarRoutes;
        if (isAdminToOrgAdmin) {
            return orgSideBarRoutes;
        } else {
            return userType === 'adminStaff' || userType === 'superAdmin' ? adminSideBarRoutes : orgSideBarRoutes;
        }
    }, [userType, isAdminToOrgAdmin]);

    const userPermissions: string[] | [] = getUserPermissionKeys(queryClient);

    const isVisible = (item: SIDE_BAR) =>
        // Check if menu is only for Org Admin
        (item.onlyForOrgAdmin ? userType === 'organizationAdmin' : true) &&
        (!item.requiredPermission ||
            userType === 'superAdmin' ||
            userType === 'organizationAdmin' ||
            userPermissions.includes(item.requiredPermission));

    // Filter sidebar based on permissions, at every depth
    const filteredRoutes = useMemo(() => {
        const filterVisible = (items: SIDE_BAR[]): SIDE_BAR[] =>
            items.filter(isVisible).map((item) => ({ ...item, children: item.children && filterVisible(item.children) }));
        return filterVisible(sideBarRoutes);
    }, [sideBarRoutes, userPermissions, userType]);

    const handleMenuCollapse = (slug: string, depth: number) => {
        setOpenSlugs((prev) => {
            if (prev[depth] === slug) return prev.slice(0, depth);
            // an ancestor open only because its branch is active has no entry in prev, so set by index
            const next = prev.slice(0, depth);
            next[depth] = slug;
            return next;
        });
    };

    // True when the current path is this item's path or the path of any descendant
    const isOnBranch = (item: SIDE_BAR, fullPath: string): boolean => location.pathname === fullPath || hasActiveDescendant(item, fullPath);

    const hasActiveDescendant = (item: SIDE_BAR, fullPath: string): boolean =>
        !!item.children?.some((child) => isOnBranch(child, fullPath + child.path));

    const renderItems = (items: SIDE_BAR[], basePath: string, depth: number) =>
        items.map((item) => {
            const fullPath = basePath + item.path;
            const hasChildren = !!item.children && item.children.length > 0;
            const isExpanded = openSlugs[depth] === item.slug || hasActiveDescendant(item, fullPath);
            // only the leaf whose path is the current path is highlighted
            const isActive = location.pathname === fullPath && !hasActiveDescendant(item, fullPath);
            // top menus always render a list (as before); nested ones only when they have children
            const hasList = depth === 0 || hasChildren;

            return (
                <li key={item.slug} className={depth === 0 ? 'nav-item' : undefined}>
                    <button
                        className={`nav-link w-100 ${hasList && !isExpanded ? 'collapsed' : ''} ${isActive ? 'active' : ''}`}
                        onClick={() => {
                            if (hasChildren) {
                                handleMenuCollapse(item.slug, depth);
                            } else {
                                navigate(fullPath);
                            }
                        }}
                    >
                        <i className={`${item.icon}`}></i>
                        <span>
                            <JTranslation text={item.title} typeCase={depth === 0 ? 'pascal' : 'none'} />
                        </span>
                        {hasChildren && <i className="ri-arrow-down-s-line ms-auto"></i>}
                    </button>
                    {hasList && (
                        <ul id={depth === 0 ? 'components-nav' : undefined} className={`nav-content collapse ${isExpanded ? 'show' : ''}`}>
                            {item.children && renderItems(item.children, fullPath, depth + 1)}
                        </ul>
                    )}
                </li>
            );
        });

    return (
        <aside id="sidebar" className="sidebar">
            <ul className="sidebar-nav" id="sidebar-nav">
                {renderItems(filteredRoutes, '', 0)}
            </ul>
        </aside>
    );
}

export default Sidebar;
